import { EvaluationContext } from '@openfeature/web-sdk';

import { loadLanguage } from 'state/intl/load-language';
import { loadRegion } from 'state/intl/load-region';
import { appVersionCode, platform, sanityDataset } from 'utils/environment';

import { getDatadogFlagsAnonymousId } from './anonymous-id';

export interface IDatadogFlagsIdentity {
  /** The account identity of the signed-in guest, if there is one. */
  cognitoId?: string | null;
  /**
   * The loyalty identity, as carried by the `custom:thLegacyCognitoId` claim.
   * Accepted with or without the `us-east-1:` prefix and stored bare.
   */
  legacyCognitoId?: string | null;
}

const IDENTITY_POOL_PREFIX = /^us-east-1:/;

/**
 * The identity a percentage rollout buckets on.
 *
 * This mirrors `deriveFlagsTargetingKey` in the app, which returns the bare
 * loyalty id for a signed-in guest who has that claim and its device id for
 * everyone else. The two clients only select the same guests when they agree
 * on this string, so the rule is copied rather than reinterpreted, and the
 * prefix is stripped because the app stores the id bare — a prefix on one
 * side alone splits the population while both clients still look correct.
 */
export const deriveDatadogFlagsTargetingKey = ({
  cognitoId,
  legacyCognitoId,
  anonymousId,
}: {
  cognitoId: string | null;
  legacyCognitoId: string | null;
  anonymousId: string;
}): string => {
  const bareLegacyCognitoId = legacyCognitoId?.replace(IDENTITY_POOL_PREFIX, '') || null;

  if (cognitoId && bareLegacyCognitoId) {
    return bareLegacyCognitoId;
  }

  return anonymousId;
};

/**
 * The attributes a targeting rule can read.
 *
 * Attribute names are deliberately the ones the app sends, so a single rule
 * authored once selects the same guests on both clients. A rule reading an
 * attribute this does not supply does not fail — it stops matching, silently,
 * and the evaluation still looks successful. That is why attributes the web
 * has no value for are sent empty rather than omitted, and why the covering
 * test asserts the resolved *variant* rather than the resolved value.
 *
 * Values are flat primitives for the same reason: a nested object is not
 * addressable by the flat attribute name a rule is written against.
 *
 * On the targeting key: a percentage rollout buckets on it, so two clients
 * only select the same population when they agree on the key. A signed-in
 * guest whose loyalty id is known agrees — both clients key on that bare id,
 * so a guest inside a 50% rollout on their phone is inside it on their
 * laptop.
 *
 * Every other guest is bucketed per client, and this is a property of the
 * identities rather than a gap to close. A guest signed in without the
 * loyalty claim falls back to a device identity on both clients, but the
 * app's is an install identity with no browser counterpart. A signed-out
 * guest has nothing shared at all. For those, a ramp reads as the configured
 * percentage of each population separately, not of one population — so a
 * rollout aimed at a single shared audience has to target signed-in guests.
 */
export const buildDatadogFlagsEvaluationContext = (
  identity: IDatadogFlagsIdentity = {}
): EvaluationContext => {
  const region = loadRegion();
  const cognitoId = identity.cognitoId || null;
  const anonymousId = getDatadogFlagsAnonymousId();

  return {
    targetingKey: deriveDatadogFlagsTargetingKey({
      cognitoId,
      legacyCognitoId: identity.legacyCognitoId ?? null,
      anonymousId,
    }),
    user_id: cognitoId ?? '',
    anonymous: !cognitoId,
    deviceId: anonymousId,
    country: region,
    language: loadLanguage(),
    sanityDataset: `${sanityDataset}_${region.toLowerCase()}`,
    platform: platform(),
    // Stated and empty. The app sends a value here; a rule written against
    // the app's shape then evaluates to "no match" on the web rather than
    // against an attribute that is not there at all.
    mobileOS: '',
    appShellVersion: '',
    appVersion: appVersionCode(),
    host: window.location.host,
    userClient: window.navigator.userAgent || '',
  };
};
